/**
 * Workforce Management — shared math.
 *
 * Pure functions used by the API (forecast generation, adherence reports) and
 * the web planner (live what-if previews), so a staffing number or an
 * adherence percentage never depends on which side computed it.
 *
 *   - Erlang C staffing: traffic → agents needed for a service-level target,
 *     capped by max occupancy, grossed up for shrinkage.
 *   - Weighted seasonal forecast: same weekday × interval over the last N
 *     weeks, recent weeks weighted higher, with a clamped trend factor.
 *   - Schedule adherence: scheduled activity blocks vs the agent's state
 *     timeline, with a grace window for short deviations.
 *   - Timezone helpers to turn a wall-clock shift start into an instant.
 */
/** Agent FSM statuses that count as logged-in, productive work. Mirrors
 *  WFM_WORK_STATES in @kc3/db (seeded into the WORK activity type). */
export const WFM_PRODUCTIVE_STATES = [
    'REGISTERED', 'IDLE', 'RESERVING', 'RESERVED', 'INVITING', 'RINGING',
    'ANSWERED', 'IN_CALL', 'ACW', 'AVAILABLE_ON_DEMAND',
];
// ---------------------------------------------------------------------------
// Erlang C
// ---------------------------------------------------------------------------
/**
 * Probability an arriving contact has to wait (Erlang C), for `trafficErlangs`
 * offered load and `agents` servers. Computed through the Erlang B recursion,
 * which stays numerically stable for hundreds of agents (no factorials).
 * Returns 1 when the queue is unstable (agents ≤ traffic).
 */
export function erlangC(trafficErlangs, agents) {
    const a = Math.max(0, trafficErlangs);
    const n = Math.floor(agents);
    if (a === 0)
        return 0;
    if (n <= a)
        return 1;
    let b = 1;
    for (let k = 1; k <= n; k++)
        b = (a * b) / (k + a * b);
    return (n * b) / (n - a * (1 - b));
}
/** Fraction (0–1) of contacts answered within `targetSec`. */
export function serviceLevelFor(trafficErlangs, agents, ahtSec, targetSec) {
    if (trafficErlangs <= 0)
        return 1;
    if (agents <= trafficErlangs || ahtSec <= 0)
        return 0;
    const pw = erlangC(trafficErlangs, agents);
    return Math.max(0, Math.min(1, 1 - pw * Math.exp((-(agents - trafficErlangs) * targetSec) / ahtSec)));
}
/** Average speed of answer, seconds. Infinity when unstable. */
export function averageSpeedOfAnswer(trafficErlangs, agents, ahtSec) {
    if (trafficErlangs <= 0)
        return 0;
    if (agents <= trafficErlangs)
        return Number.POSITIVE_INFINITY;
    return (erlangC(trafficErlangs, agents) * ahtSec) / (agents - trafficErlangs);
}
const round2 = (n) => Math.round(n * 100) / 100;
export function requiredStaffing(input) {
    const { volume, ahtSec, intervalSec } = input;
    if (!(volume > 0) || !(ahtSec > 0) || !(intervalSec > 0)) {
        return { trafficErlangs: 0, agents: 0, staff: 0, serviceLevel: 100, occupancy: 0, asaSec: 0 };
    }
    const a = (volume * ahtSec) / intervalSec;
    const target = Math.min(0.999, Math.max(0.01, input.serviceLevelPct / 100));
    const maxOcc = Math.min(1, Math.max(0.1, input.maxOccupancyPct / 100));
    let n = Math.max(1, Math.ceil(a / maxOcc), Math.floor(a) + 1);
    const cap = Math.ceil(a) + 1000;
    while (n < cap && serviceLevelFor(a, n, ahtSec, input.serviceLevelSec) < target)
        n++;
    const shrink = Math.min(0.9, Math.max(0, input.shrinkagePct / 100));
    return {
        trafficErlangs: round2(a),
        agents: n,
        staff: Math.ceil(n / (1 - shrink) - 1e-9),
        serviceLevel: round2(serviceLevelFor(a, n, ahtSec, input.serviceLevelSec) * 100),
        occupancy: round2((a / n) * 100),
        asaSec: round2(averageSpeedOfAnswer(a, n, ahtSec)),
    };
}
export function weekdayOf(date) {
    return new Date(`${date}T12:00:00Z`).getUTCDay();
}
export function addDays(date, delta) {
    const d = new Date(`${date}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + delta);
    return d.toISOString().slice(0, 10);
}
export function daysBetween(from, to) {
    return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}
export function weightedSeasonalForecast(input) {
    const step = input.intervalMinutes;
    const slotsPerDay = Math.floor(1440 / step);
    const byDateSlot = new Map();
    const dailyTotal = new Map();
    let firstDataDate = null;
    let handledAll = 0;
    let handleSecAll = 0;
    for (const p of input.history) {
        byDateSlot.set(`${p.date}|${p.slot}`, p);
        dailyTotal.set(p.date, (dailyTotal.get(p.date) ?? 0) + p.volume);
        if (p.volume > 0 && (firstDataDate === null || p.date < firstDataDate))
            firstDataDate = p.date;
        handledAll += p.handled;
        handleSecAll += p.handleSec;
    }
    const overallAhtSec = handledAll > 0 ? handleSecAll / handledAll : (input.defaultAhtSec ?? 180);
    // Trend: weekly totals inside the window, oldest → newest. Weeks before the
    // first day with any traffic are "system not live yet", not zero demand.
    const weekly = [];
    const totalDays = daysBetween(input.historyStart, input.historyEnd) + 1;
    for (let w = 0; w * 7 < totalDays; w++) {
        let sum = 0;
        let live = false;
        for (let d = 0; d < 7; d++) {
            const date = addDays(input.historyStart, w * 7 + d);
            if (date > input.historyEnd)
                break;
            if (firstDataDate !== null && date >= firstDataDate)
                live = true;
            sum += dailyTotal.get(date) ?? 0;
        }
        if (live)
            weekly.push(sum);
    }
    let trendFactor = 1;
    if (weekly.length >= 4) {
        const half = Math.floor(weekly.length / 2);
        const older = weekly.slice(0, half).reduce((s, v) => s + v, 0) / half;
        const recent = weekly.slice(weekly.length - half).reduce((s, v) => s + v, 0) / half;
        if (older > 0)
            trendFactor = Math.min(1.25, Math.max(0.8, recent / older));
    }
    const uplift = trendFactor * (1 + (input.growthPct ?? 0) / 100);
    const points = [];
    for (const target of input.targetDates) {
        // Same weekday, walking back week by week from the newest in-window date.
        const sameDays = [];
        let d = target;
        while (d > input.historyEnd)
            d = addDays(d, -7);
        for (let i = 0; i < input.historyWeeks && d >= input.historyStart; i++, d = addDays(d, -7)) {
            if (firstDataDate === null || d >= firstDataDate)
                sameDays.push(d);
        }
        for (let s = 0; s < slotsPerDay; s++) {
            const slot = s * step;
            let wSum = 0;
            let vSum = 0;
            let handled = 0;
            let handleSec = 0;
            sameDays.forEach((day, k) => {
                const w = sameDays.length - k;
                const p = byDateSlot.get(`${day}|${slot}`);
                wSum += w;
                vSum += w * (p?.volume ?? 0);
                handled += p?.handled ?? 0;
                handleSec += p?.handleSec ?? 0;
            });
            const volume = wSum > 0 ? (vSum / wSum) * uplift : 0;
            points.push({
                date: target,
                slot,
                volume: round2(volume),
                ahtSec: round2(handled >= 3 ? handleSec / handled : overallAhtSec),
            });
        }
    }
    return { points, trendFactor: round2(trendFactor), weeksWithData: weekly.length, overallAhtSec: round2(overallAhtSec) };
}
export function stateSpans(states, initialStatus, from, to) {
    const spans = [];
    let cur = initialStatus;
    let at = from;
    for (const s of states) {
        if (s.at <= from) {
            cur = s.status;
            continue;
        }
        if (s.at >= to)
            break;
        if (s.at > at)
            spans.push({ start: at, end: s.at, status: cur });
        cur = s.status;
        at = s.at;
    }
    if (to > at)
        spans.push({ start: at, end: to, status: cur });
    return spans;
}
export function computeAdherence(input) {
    const { from, to } = input;
    const spans = stateSpans(input.states, input.initialStatus, from, to);
    const productive = new Set(WFM_PRODUCTIVE_STATES);
    const timeInStateMs = {};
    let loggedInMs = 0;
    let productiveMs = 0;
    for (const s of spans) {
        const d = s.end - s.start;
        timeInStateMs[s.status] = (timeInStateMs[s.status] ?? 0) + d;
        if (s.status !== 'OFFLINE')
            loggedInMs += d;
        if (productive.has(s.status))
            productiveMs += d;
    }
    let scheduledMs = 0;
    let adherentMs = 0;
    let scheduledWorkMs = 0;
    const exceptions = [];
    for (const b of input.blocks) {
        const bs = Math.max(b.start, from);
        const be = Math.min(b.end, to);
        if (be <= bs)
            continue;
        scheduledMs += be - bs;
        if (b.category === 'WORK')
            scheduledWorkMs += be - bs;
        const allowed = new Set(b.allowedStates);
        const pending = [];
        for (const s of spans) {
            const ps = Math.max(bs, s.start);
            const pe = Math.min(be, s.end);
            if (pe <= ps)
                continue;
            if (allowed.has(s.status)) {
                adherentMs += pe - ps;
                continue;
            }
            const last = pending[pending.length - 1];
            if (last && last.end === ps && last.actual === s.status) {
                last.end = pe;
                last.durationMs = pe - last.start;
            }
            else {
                pending.push({ start: ps, end: pe, durationMs: pe - ps, expected: b.activityName, category: String(b.category), actual: s.status });
            }
        }
        for (const ex of pending) {
            if (ex.durationMs < input.graceMs)
                adherentMs += ex.durationMs;
            else
                exceptions.push(ex);
        }
    }
    exceptions.sort((x, y) => x.start - y.start);
    return {
        scheduledMs,
        adherentMs,
        outOfAdherenceMs: scheduledMs - adherentMs,
        adherencePct: scheduledMs > 0 ? round2((adherentMs / scheduledMs) * 100) : null,
        loggedInMs,
        productiveMs,
        scheduledWorkMs,
        conformancePct: scheduledWorkMs > 0 ? round2((productiveMs / scheduledWorkMs) * 100) : null,
        exceptions,
        timeInStateMs,
    };
}
/** Statuses that mean "handling a contact" — a break due during one is deferred, not missed. */
export const WFM_CONTACT_STATES = [
    'RESERVING', 'RESERVED', 'INVITING', 'RINGING', 'ANSWERED', 'IN_CALL', 'ACW',
];
const BREAKABLE = new Set(['BREAK', 'LUNCH']);
/**
 * Break deferral: when a BREAK/LUNCH block is due while the agent is handling
 * a contact (ringing, on a call, in wrap-up), the break slides to start when
 * that contact ends and keeps its full scheduled length. The time spent on
 * the contact becomes work (adherent), so an agent is never penalised for
 * finishing a customer's call, and never loses break time to it.
 *
 * Rules:
 *   - only breaks whose scheduled start has passed are considered;
 *   - the shift is not extended: the moved break is clipped at the next
 *     non-work activity or the end of the shift;
 *   - a contact still running at `now` leaves the break `pending`, provisionally
 *     starting at `now` (it keeps sliding until the contact ends);
 *   - deferral is capped at `maxDeferMs` (default 2 h).
 */
export function applyBreakDeferral(blocks, states, initialStatus, now, opts = {}) {
    const out = [...blocks].sort((a, b) => a.start - b.start).map((b) => ({ ...b }));
    if (out.length === 0)
        return out;
    const contact = new Set(opts.contactStates ?? WFM_CONTACT_STATES);
    const maxDefer = opts.maxDeferMs ?? 2 * 3_600_000;
    const from = out[0].start - 6 * 3_600_000;
    const spans = stateSpans(states, initialStatus, from, Math.max(now, from + 1));
    const statusAt = (t) => spans.find((s) => s.start <= t && t < s.end)?.status ?? (spans[spans.length - 1]?.status ?? initialStatus);
    /** End of the contact run covering `t`; null if it is still running at now. */
    const contactEnd = (t) => {
        for (const s of spans) {
            if (s.end <= t)
                continue;
            if (!contact.has(s.status))
                return Math.max(s.start, t);
        }
        return null;
    };
    for (let i = 0; i < out.length; i++) {
        const b = out[i];
        if (!BREAKABLE.has(String(b.category)) || b.start > now)
            continue;
        if (!contact.has(statusAt(b.start)))
            continue;
        const end = contactEnd(b.start);
        const pending = end === null;
        const defer = Math.min((end ?? now) - b.start, maxDefer);
        if (defer <= 0)
            continue;
        // Furthest the break may extend: through the following WORK blocks, up to
        // the next non-work activity / end of the contiguous shift.
        let limit = b.end;
        for (let j = i + 1; j < out.length && out[j].start <= limit && out[j].category === 'WORK'; j++)
            limit = out[j].end;
        const length = b.end - b.start;
        const newStart = Math.min(b.start + defer, limit);
        const newEnd = Math.min(newStart + length, limit);
        const work = (out[i - 1]?.category === 'WORK' ? out[i - 1] : out.slice(i + 1).find((x) => x.category === 'WORK')) ?? null;
        const moved = { ...b, start: newStart, end: newEnd, scheduledStart: b.scheduledStart ?? b.start, deferredMs: newStart - (b.scheduledStart ?? b.start), pending };
        const before = [];
        const prev = out[i - 1];
        if (prev && prev.category === 'WORK' && prev.end === b.start)
            prev.end = newStart;
        else if (work && newStart > b.start)
            before.push({ ...work, start: b.start, end: newStart });
        const after = out.slice(i + 1).flatMap((x) => {
            if (x.category !== 'WORK' || x.start >= newEnd)
                return [x];
            return x.end <= newEnd ? [] : [{ ...x, start: newEnd }];
        });
        out.splice(i, out.length - i, ...before, moved, ...after);
        i += before.length;
    }
    return out.filter((b) => b.end > b.start);
}
/**
 * A shift's activities laid over its span, with every uncovered gap filled by
 * the default WORK activity. Overlapping activities are clipped to the shift
 * and to each other (earliest start wins).
 */
export function fillShiftBlocks(shift, activities, work) {
    const sorted = [...activities]
        .map((a) => ({ ...a, start: Math.max(a.start, shift.start), end: Math.min(a.end, shift.end) }))
        .filter((a) => a.end > a.start)
        .sort((x, y) => x.start - y.start);
    const out = [];
    let cursor = shift.start;
    for (const a of sorted) {
        const start = Math.max(a.start, cursor);
        if (start >= a.end)
            continue;
        if (start > cursor)
            out.push({ ...work, start: cursor, end: start });
        out.push({ ...a, start, end: a.end });
        cursor = a.end;
    }
    if (cursor < shift.end)
        out.push({ ...work, start: cursor, end: shift.end });
    return out;
}
// ---------------------------------------------------------------------------
// Timezone helpers
// ---------------------------------------------------------------------------
/** Offset (ms) of `tz` from UTC at instant `ms`. */
export function tzOffsetMs(ms, tz) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(new Date(ms));
    const get = (t) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    return asUtc - Math.floor(ms / 1000) * 1000;
}
/** The instant a wall-clock `date` + `time` (HH:MM[:SS]) occurs in `tz`. */
export function wallTimeToInstant(date, time, tz) {
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm, ss] = time.split(':').map(Number);
    const guess = Date.UTC(y, m - 1, d, hh, mm, ss ?? 0);
    const first = guess - tzOffsetMs(guess, tz);
    // Re-resolve once so a DST change between guess and answer is honoured.
    return guess - tzOffsetMs(first, tz);
}
/** Local calendar date (YYYY-MM-DD) of `ms` in `tz`. */
export function localDateOf(ms, tz) {
    return new Date(ms).toLocaleDateString('en-CA', { timeZone: tz });
}
//# sourceMappingURL=wfm.js.map